// Copyright (c) 2023 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { catchError, filter, mergeMap } from "rxjs/operators"
import { Action } from "redux"
import { t } from "@lingui/macro"
import { create } from "@bufbuild/protobuf"
import * as Actions from "../actions"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { showSnackBar } from "../../snackbar/actions"
import { assistantService, Code, executor, Init, isConnectError } from "../../../app/rpc"
import {
  GenerateNarrativeRequestSchema,
  GenerateNarrativeResponse,
  TranslationSchema,
} from "../../../generated/wisetime/user/assistant/v4/assistant_pb"

type GenerateNarrativeRpc = (request: Init<typeof GenerateNarrativeRequestSchema>) => Observable<GenerateNarrativeResponse>

export const generateNarrative = (generateNarrativeRpc: GenerateNarrativeRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(Actions.generateNarrative.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(generateNarrativeRpc),
          mergeMap(response => {
            const [narrative] = response.narrative
            if (narrative) {
              return of(TimelineAdditionalUiSlice.setPendingNarratives([
                create(TranslationSchema, { key: action.payload.unpostedTimeGroupId, text: narrative }),
              ]))
            }
            return of(TimelineAdditionalUiSlice.deletePendingNarratives([action.payload.unpostedTimeGroupId ?? ""]))
          }),
          catchError((e) => handleError(e, action.payload)),
        )
      ),
    )

const handleError = (e: unknown, payload: Init<typeof GenerateNarrativeRequestSchema>): Observable<Action> => {
  const actions: Action[] = [
    TimelineAdditionalUiSlice.deletePendingNarratives([payload.unpostedTimeGroupId ?? ""]),
  ]
  if (isConnectError(e)) {
    const { code } = e
    if (code === Code.PermissionDenied) {
      actions.push(showSnackBar({
        message: t({ id: "msg_generate_narrative_permission_denied", message: "Failed to generate narrative. You do not have permission." }),
        messageType: "ERROR",
      }))
      return concat(actions)
    }
    if (code === Code.NotFound) {
      actions.push(
        showSnackBar({
          message: t({ id: "msg_generate_narrative_not_found", message: "Failed to generate narrative. Could not find timesheet." }),
          messageType: "ERROR",
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
    if (code === Code.FailedPrecondition) {
      actions.push(
        showSnackBar({
          message: t({ id: "msg_generate_narrative_failed_precondition", message: "Failed to generate narrative. Timesheet has already been posted." }),
          messageType: "ERROR"
        }),
        Actions.onTimelineOutOfSync(),
      )
      return concat(actions)
    }
  }
  actions.push(showSnackBar({
    message: t({ id: "msg_generate_narrative_failed", message: "Failed to generate narrative. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
  return concat(actions)
}

const generateNarrativeRpc = (request: Init<typeof GenerateNarrativeRequestSchema>): Observable<GenerateNarrativeResponse> =>
  executor(() => assistantService.generateNarrative(create(GenerateNarrativeRequestSchema, request)))

export default generateNarrative(generateNarrativeRpc)
